import React, { useState } from "react";
import { customFetch } from '../apiClient';

const UserRolesManager = () => {
  const [loginId, setLoginId] = useState("");
  const [modulesData, setModulesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchPerformed, setSearchPerformed] = useState(false);
  const hospitalId = sessionStorage.getItem("hospitalId");

  const fetchUserRoles = async () => {
    if (!loginId.trim()) {
      setError("Please enter Login ID");
      return;
    }
    setError("");
    setSuccess("");
    setLoading(true);
    setSearchPerformed(true);
    
    try {
     const res = await customFetch(`/hospitals/${hospitalId}/roles/user/${loginId}`);
      if (!res.ok) throw new Error("Failed to fetch user roles");
      const result = await res.json();

      const mappedData = result.map((m) => ({
        mid: m.modules,
        modules: m.moduleName,
        addRecord: m.addRecord === "Y",
        editRecord: m.editRecord === "Y",
        deleteRecord: m.deleteRecord === "Y",
      }));

      setModulesData(mappedData);
      if (mappedData.length === 0) {
        setError("No modules found for this user");
      }
    } catch (e) {
      setError(e.message || "Failed to fetch user roles");
      setModulesData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (mid, type) => (e) => {
    const checked = e.target.checked;
    setModulesData((prev) =>
      prev.map((module) =>
        module.mid === mid ? { ...module, [type]: checked } : module
      )
    );
  };

  const handleSubmit = async () => {
    if (!loginId.trim()) {
      setError("Login ID missing");
      return;
    }
    setError("");
    setSuccess("");
    setSaving(true);

    try {
   
      const payload = modulesData.map(module => ({
        Modules: module.mid,
        AddRecord: module.addRecord ? "Y" : "N",
        EditRecord: module.editRecord ? "Y" : "N",
        DeleteRecord: module.deleteRecord ? "Y" : "N",
        LoginID: loginId,
        HID: parseInt(hospitalId),
        DelPass: "123", 
        EditPass: "123"
      }));

    const res = await customFetch(`/hospitals/${hospitalId}/roles`, {
  method: "POST",
  body: JSON.stringify(payload),
});

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to save user roles");
      }
      
      setSuccess("Roles saved successfully!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) {
      setError(e.message || "Failed to save roles");
    } finally {
      setSaving(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      fetchUserRoles();
    }
  };

  const toggleAllPermissions = (permissionType) => {
    const allChecked = modulesData.every(m => m[permissionType]);
    setModulesData(prev =>
      prev.map(module => ({
        ...module,
        [permissionType]: !allChecked
      }))
    );
  };

  const selectAllPermissions = () => {
    setModulesData(prev =>
      prev.map(module => ({
        ...module,
        addRecord: true,
        editRecord: true,
        deleteRecord: true
      }))
    );
  };

  const clearAllPermissions = () => {
    setModulesData(prev =>
      prev.map(module => ({
        ...module,
        addRecord: false,
        editRecord: false,
        deleteRecord: false
      }))
    );
  };

  const getPermissionStats = () => {
    const total = modulesData.length;
    const addCount = modulesData.filter(m => m.addRecord).length;
    const editCount = modulesData.filter(m => m.editRecord).length;
    const deleteCount = modulesData.filter(m => m.deleteRecord).length;
    return { total, addCount, editCount, deleteCount };
  };

  const stats = getPermissionStats();

  return (
    <div className="roles-manager-container">
      <div className="roles-card">
        {/* Header */}
        <div className="roles-header">
          <div className="header-left">
            <div className="header-icon">
              <i className="bi bi-shield-lock-fill"></i>
            </div>
            <div className="header-text">
              <h5>Role Management</h5>
            </div>
          </div>
          {modulesData.length > 0 && (
            <div className="header-stats">
              <div className="stat-item">
                <i className="bi bi-boxes"></i>
                <span>{stats.total} Modules</span>
              </div>
            </div>
          )}
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert-custom alert-danger">
            <i className="bi bi-exclamation-triangle-fill"></i>
            <span>{error}</span>
            <button className="alert-close" onClick={() => setError('')}><i className="bi bi-x"></i></button>
          </div>
        )}
        {success && (
          <div className="alert-custom alert-success">
            <i className="bi bi-check-circle-fill"></i>
            <span>{success}</span>
            <button className="alert-close" onClick={() => setSuccess('')}><i className="bi bi-x"></i></button>
          </div>
        )}

        {/* Search Toolbar */}
        <div className="search-toolbar">
          <div className="search-input-group">
            <i className="bi bi-person-badge"></i>
            <input
              id="loginIdInput"
              type="text"
              className="search-input"
              placeholder="Enter Login ID..."
              value={loginId}
              onChange={(e) => setLoginId(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
            />
            <button className="btn-search" onClick={fetchUserRoles} disabled={loading || !loginId.trim()}>
              {loading ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-search"></i>}
              Search
            </button>
          </div>
        </div>

        <div className="roles-body">
          {loading && (
            <div className="loading-state">
              <div className="spinner-custom"></div>
              <p>Loading permissions...</p>
            </div>
          )}

          {!loading && searchPerformed && modulesData.length === 0 && (
            <div className="empty-state">
              <p>No modules assigned to this user.</p>
            </div>
          )}

          {!loading && !searchPerformed && (
            <div className="empty-state">
              <p>Enter a Login ID to fetch permissions.</p>
            </div>
          )}

          {!loading && modulesData.length > 0 && (
            <>
              {/* Quick Actions */}
              <div className="quick-actions-bar">
                <div className="actions-left">
                  <button className="btn-quick-action" onClick={selectAllPermissions}>
                    <i className="bi bi-check-all"></i> Grant All
                  </button>
                  <button className="btn-quick-action rev" onClick={clearAllPermissions}>
                    <i className="bi bi-x-circle"></i> Revoke All
                  </button>
                </div>
                <div className="actions-right">
                  <div className="permission-summary">
                    <span className="summary-badge add">{stats.addCount}/{stats.total} Add</span>
                    <span className="summary-badge edit">{stats.editCount}/{stats.total} Edit</span>
                    <span className="summary-badge del">{stats.deleteCount}/{stats.total} Delete</span>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="permissions-table-container">
                <table className="permissions-table">
                  <thead>
                    <tr>
                      <th className="module-col">Module Name</th>
                      <th className="perm-col">
                        <div className="th-content">
                          Add 
                          <button className="toggle-all-btn" onClick={() => toggleAllPermissions('addRecord')} title="Toggle All">
                            <i className="bi bi-arrow-down-up"></i>
                          </button>
                        </div>
                      </th>
                      <th className="perm-col">
                        <div className="th-content">
                          Edit 
                          <button className="toggle-all-btn" onClick={() => toggleAllPermissions('editRecord')} title="Toggle All">
                            <i className="bi bi-arrow-down-up"></i>
                          </button>
                        </div>
                      </th>
                      <th className="perm-col">
                        <div className="th-content">
                          Delete 
                          <button className="toggle-all-btn" onClick={() => toggleAllPermissions('deleteRecord')} title="Toggle All">
                            <i className="bi bi-arrow-down-up"></i>
                          </button>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {modulesData.map((module, index) => (
                      <tr key={module.mid}>
                        <td>
                          <div className="module-info">
                            <span className="module-badge">{index + 1}</span>
                            <span className="module-name">{module.modules}</span>
                          </div>
                        </td>
                        <td className="center-cell">
                          <label className="toggle-switch">
                            <input type="checkbox" checked={module.addRecord} onChange={handleCheckboxChange(module.mid, "addRecord")} />
                            <span className="toggle-slider add"></span>
                          </label>
                        </td>
                        <td className="center-cell">
                          <label className="toggle-switch">
                            <input type="checkbox" checked={module.editRecord} onChange={handleCheckboxChange(module.mid, "editRecord")} />
                            <span className="toggle-slider edit"></span>
                          </label>
                        </td>
                        <td className="center-cell">
                          <label className="toggle-switch">
                            <input type="checkbox" checked={module.deleteRecord} onChange={handleCheckboxChange(module.mid, "deleteRecord")} />
                            <span className="toggle-slider del"></span>
                          </label>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Save */}
              <div className="save-section">
                <button className="btn-save" onClick={handleSubmit} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Permissions'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <style jsx>{`
        .roles-manager-container {
          padding: 0;
          font-family: system-ui, -apple-system, sans-serif;
        }

        .roles-card {
          background: white;
          border-radius: 6px;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
          border: 1px solid #e2e8f0;
          overflow: hidden;
        }

        /* Header */
        .roles-header {
          background: var(--theme-color, #0088cc);
          padding: 8px 12px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 2px solid #00d4aa;
        }

        .header-left { display: flex; align-items: center; gap: 8px; }
        .header-icon {
          width: 24px; height: 24px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 4px; display: flex; align-items: center; justify-content: center;
          font-size: 14px; color: white;
        }
        .header-text h5 { margin: 0; color: white; font-size: 14px; font-weight: 600; }

        .header-stats { display: flex; gap: 10px; }
        .stat-item {
          background: rgba(255, 255, 255, 0.2);
          padding: 4px 8px; border-radius: 4px;
          display: flex; align-items: center; gap: 6px;
          color: white; font-size: 11px; font-weight: 600;
        }

        /* Alerts */
        .alert-custom {
          margin: 8px 12px 0; padding: 6px 10px; border-radius: 4px;
          display: flex; align-items: center; gap: 6px; font-size: 12px; position: relative;
        }
        .alert-danger { background: #fff5f5; color: #e53e3e; border: 1px solid #feb2b2; }
        .alert-success { background: #f0fff4; color: #27ae60; border: 1px solid #9ae6b4; }
        .alert-close { position: absolute; right: 8px; background: none; border: none; cursor: pointer; opacity: 0.6; }

        /* Search Toolbar */
        .search-toolbar {
          padding: 8px 12px;
          background: #f7fafc;
          border-bottom: 1px solid #e2e8f0;
        }

        .search-input-group {
          display: flex;
          align-items: center;
          gap: 8px;
          max-width: 400px;
          position: relative;
        }

        .search-input-group > i {
          position: absolute; left: 10px; color: #a0aec0; font-size: 14px;
        }

        .search-input {
          flex: 1; padding: 6px 10px 6px 30px;
          border: 1px solid #cbd5e0; border-radius: 4px;
          font-size: 12px; outline: none;
        }
        .search-input:focus { border-color: var(--theme-color, #3182ce); box-shadow: 0 0 0 1px var(--theme-color, #3182ce); }

        .btn-search {
          padding: 6px 14px; border-radius: 4px; border: none;
          background: var(--theme-color, #3182ce); color: white;
          font-size: 12px; font-weight: 600; cursor: pointer;
          display: flex; align-items: center; gap: 6px;
        }
        .btn-search:disabled { opacity: 0.6; cursor: not-allowed; }

        /* Body & Quick Actions */
        .roles-body { padding: 12px; }

        .quick-actions-bar {
          display: flex; justify-content: space-between; align-items: center;
          background: #f7fafc; padding: 8px 12px; border: 1px solid #e2e8f0;
          border-radius: 4px; margin-bottom: 12px; flex-wrap: wrap; gap: 8px;
        }

        .actions-left { display: flex; gap: 8px; }
        .btn-quick-action {
          padding: 4px 10px; border-radius: 4px;
          border: 1px solid var(--theme-color, #3182ce); background: white; color: var(--theme-color, #3182ce);
          font-size: 11px; font-weight: 600; cursor: pointer;
          display: flex; align-items: center; gap: 4px;
        }
        .btn-quick-action.rev { border-color: #e53e3e; color: #e53e3e; }
        .btn-quick-action:hover { background: var(--theme-color, #3182ce); color: white; }
        .btn-quick-action.rev:hover { background: #e53e3e; color: white; }

        .permission-summary { display: flex; gap: 8px; }
        .summary-badge { padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 600; }
        .summary-badge.add { background: #c6f6d5; color: #22543d; }
        .summary-badge.edit { background: #bee3f8; color: #2a4365; }
        .summary-badge.del { background: #fed7d7; color: #742a2a; }

        /* Table */
        .permissions-table-container {
          border: 1px solid #e2e8f0; border-radius: 4px; overflow-x: auto; margin-bottom: 12px;
        }

        .permissions-table { width: 100%; border-collapse: collapse; font-size: 12px; text-align: left; }
        .permissions-table thead { background: #edf2f7; border-bottom: 1px solid #cbd5e0; }
        .permissions-table th { padding: 8px 10px; font-weight: 600; color: #4a5568; white-space: nowrap; }
        .permissions-table td { padding: 6px 10px; border-bottom: 1px solid #e2e8f0; color: #2d3748; }
        .permissions-table tbody tr:hover { background: #f7fafc; }

        .module-col { width: auto; }
        .perm-col { width: 100px; text-align: center; }
        .center-cell { text-align: center; }

        .th-content { display: flex; align-items: center; justify-content: center; gap: 6px; }
        .toggle-all-btn {
          background: #e2e8f0; border: none; padding: 2px 4px; border-radius: 3px;
          cursor: pointer; font-size: 10px; color: #4a5568;
        }
        .toggle-all-btn:hover { background: #cbd5e0; }

        .module-info { display: flex; align-items: center; gap: 8px; }
        .module-badge {
          background: var(--theme-color, #3182ce); color: white; width: 20px; height: 20px;
          display: flex; align-items: center; justify-content: center; border-radius: 4px; font-size: 10px; font-weight: 600;
        }
        .module-name { font-weight: 500; }

        /* Compact Toggle Switch */
        .toggle-switch { position: relative; display: inline-block; width: 32px; height: 18px; cursor: pointer; }
        .toggle-switch input { opacity: 0; width: 0; height: 0; }
        .toggle-slider {
          position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0;
          background-color: #cbd5e0; transition: 0.2s; border-radius: 30px;
        }
        .toggle-slider:before {
          position: absolute; content: ""; height: 14px; width: 14px; left: 2px; bottom: 2px;
          background-color: white; transition: 0.2s; border-radius: 50%;
        }
        input:checked + .toggle-slider.add { background-color: #38a169; }
        input:checked + .toggle-slider.edit { background-color: #3182ce; }
        input:checked + .toggle-slider.del { background-color: #e53e3e; }
        input:checked + .toggle-slider:before { transform: translateX(14px); }

        /* Save Section & Loaders */
        .save-section { display: flex; justify-content: flex-end; }
        .btn-save {
          padding: 8px 16px; border-radius: 4px; border: none;
          background: #38a169; color: white; font-size: 12px; font-weight: 600; cursor: pointer;
        }
        .btn-save:disabled { opacity: 0.6; cursor: not-allowed; }

        .loading-state, .empty-state { text-align: center; padding: 30px 10px; font-size: 12px; color: #718096; }
        .spinner-custom {
          width: 24px; height: 24px; border: 3px solid #edf2f7; border-top-color: var(--theme-color, #3182ce);
          border-radius: 50%; margin: 0 auto 10px; animation: spin 1s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* Responsive */
        @media (max-width: 600px) {
          .quick-actions-bar { flex-direction: column; align-items: stretch; }
          .actions-left, .permission-summary { justify-content: space-between; }
        }
      `}</style>
    </div>
  );
};

export default UserRolesManager;